import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Shield,
  Sun,
  Cog,
  Layers,
  Armchair,
  Square,
  Truck,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import ParticlesBackground from "../components/ParticlesBackground";
import { services, cars } from "../data/staticData";

const iconMap = {
  sparkles: Sparkles,
  shield: Shield,
  sun: Sun,
  cog: Cog,
  layers: Layers,
  armchair: Armchair,
  square: Square,
  truck: Truck,
};

const Home = () => {
  // Usamos datos estáticos
  const displayServices = services.slice(0, 6);
  const displayCars = cars.slice(0, 4);

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <ParticlesBackground />
        
        {/* Hero background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1610698517225-78fcfe40aceb?q=80&w=2070&auto=format&fit=crop"
            alt="Hero car"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/60 to-[#050505]" />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black tracking-tighter mb-4 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="text-white">BRILL</span>
              <span className="text-[#00A8FF] text-glow">OX</span>
            </motion.h1>

            <motion.p
              className="text-base sm:text-lg md:text-xl lg:text-3xl font-light text-white/80 mb-3 sm:mb-4 uppercase tracking-widest px-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Detailing Premium para Vehículos
            </motion.p>

            <motion.p
              className="text-sm sm:text-base md:text-lg lg:text-xl text-[#00A8FF] font-light italic mb-8 sm:mb-12 px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              "La sensación de volver a estrenarlo"
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <Link to="/servicios" data-testid="hero-services-btn">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-skew bg-[#00A8FF] text-[#050505] px-8 py-4 font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,168,255,0.5)] transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    Servicios
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </motion.button>
              </Link>
              <Link to="/contacto" data-testid="hero-contact-btn">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-skew border-2 border-[#00A8FF]/50 text-[#00A8FF] px-8 py-4 font-bold uppercase tracking-widest hover:bg-[#00A8FF]/10 hover:border-[#00A8FF] transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    Contacto
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-[#00A8FF]/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              className="w-1.5 h-1.5 bg-[#00A8FF] rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-24 relative overflow-hidden" data-testid="about-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter mb-4 sm:mb-6">
                <span className="text-white">Sobre </span>
                <span className="text-[#00A8FF]">Nosotros</span>
              </h2>
              <p className="text-white/70 text-sm sm:text-base lg:text-lg font-light leading-relaxed mb-4 sm:mb-6">
                En Brillox nos especializamos en el cuidado premium de vehículos.
                Utilizamos las técnicas más avanzadas y productos de la más alta
                calidad para transformar tu coche en una verdadera obra de arte.
              </p>
              <p className="text-white/70 text-sm sm:text-base lg:text-lg font-light leading-relaxed mb-6 sm:mb-8">
                Nuestro equipo de profesionales cuenta con años de experiencia en
                el sector del detailing, garantizando resultados excepcionales en
                cada trabajo que realizamos.
              </p>
              <div className="grid grid-cols-3 gap-3 sm:gap-6">
                {[
                  { value: "500+", label: "Vehículos" },
                  { value: "1", label: "Año" },
                  { value: "100%", label: "Satisfacción" },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    className="glass-card p-3 sm:p-4 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                  >
                    <div className="text-xl sm:text-2xl md:text-3xl font-black text-[#00A8FF]">{stat.value}</div>
                    <div className="text-white/60 text-xs sm:text-sm uppercase tracking-wider">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_gallery-services/artifacts/nmd65mxa_image.png"
                  alt="Taller Brillox"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 border-2 border-[#00A8FF]/30 translate-x-4 translate-y-4 -z-10" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 relative" data-testid="services-preview-section">
        <div className="absolute inset-0 hero-gradient opacity-50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter mb-3 sm:mb-4">
              <span className="text-white">Nuestros </span>
              <span className="text-[#00A8FF]">Servicios</span>
            </h2>
            <p className="text-white/60 text-sm sm:text-base lg:text-lg font-light max-w-2xl mx-auto px-2">
              Ofrecemos una amplia gama de servicios de detailing para mantener tu vehículo en perfectas condiciones
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayServices.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Sparkles;
              return (
                <motion.div
                  key={service.id}
                  className="glass-card group cursor-pointer overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 glass rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#00A8FF]" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#00A8FF] transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-white/60 font-light text-sm line-clamp-2">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/servicios" data-testid="view-all-services-btn">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-skew border-2 border-[#00A8FF]/50 text-[#00A8FF] px-8 py-4 font-bold uppercase tracking-widest hover:bg-[#00A8FF]/10 hover:border-[#00A8FF] transition-all duration-300"
              >
                <span className="flex items-center gap-2">
                  Ver Todos los Servicios
                  <ChevronRight className="w-5 h-5" />
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Gallery Preview */}
      {displayCars.length > 0 && (
        <section className="py-24 relative" data-testid="gallery-preview-section">
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter mb-3 sm:mb-4">
                <span className="text-white">Nuestra </span>
                <span className="text-[#00A8FF]">Galería</span>
              </h2>
              <p className="text-white/60 text-sm sm:text-base lg:text-lg font-light max-w-2xl mx-auto px-2">
                Descubre las transformaciones que hemos realizado en nuestra galería de trabajos
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayCars.map((car, index) => (
                <Link to={`/galeria/${car.id}`} key={car.id} data-testid={`gallery-preview-${car.id}`}>
                  <motion.div
                    className="glass-card group cursor-pointer overflow-hidden"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={car.thumbnail}
                        alt={car.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Hover overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-full h-full bg-[#00A8FF]/10 backdrop-blur-sm flex items-center justify-center">
                          <span className="text-white font-bold uppercase tracking-wider">Ver Detalle</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-white group-hover:text-[#00A8FF] transition-colors">
                        {car.name}
                      </h3>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>

            <motion.div
              className="text-center mt-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/galeria" data-testid="explore-gallery-btn">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-skew bg-[#00A8FF] text-[#050505] px-8 py-4 font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,168,255,0.5)] transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    Explorar Galería Completa
                    <ChevronRight className="w-5 h-5" />
                  </span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1565351206078-bb912481dcdd?q=80&w=2070&auto=format&fit=crop"
            alt="Workshop"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter mb-4 sm:mb-6 px-2">
              <span className="text-white">¿Quieres un resultado </span>
              <span className="text-[#00A8FF]">como nuevo?</span>
            </h2>
            <p className="text-white/60 text-sm sm:text-base lg:text-lg font-light mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              ¡Hablemos! Contacta con nosotros y descubre cómo podemos transformar tu vehículo
            </p>
            <Link to="/contacto" data-testid="cta-contact-btn">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-skew bg-[#00A8FF] text-[#050505] px-10 py-5 font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,168,255,0.5)] transition-all duration-300 text-lg"
              >
                <span className="flex items-center gap-3">
                  Ir a Contacto
                  <ArrowRight className="w-6 h-6" />
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
